import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Field,
    Input,
    makeStyles,
    Spinner,
    Textarea,
} from "@fluentui/react-components";
import { MessageBarType } from "@fluentui/react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";

// component functions
import * as ComponentFunctions from "./NegotiationAddendum.Form.Functions";

// components
import { MessageComponent } from "../../../../commons/component/MessageComponent";
import { DateTimeInput } from "../../../../commons/component/Inputs/DateTimeInput/DateTimeInput";
import { NewContractAddendum, ContractAddendumType } from "../../../../models/Entities/Contracts/NewContractAddendum";
import { ContractAddendum } from "../../../../models/Entities/Contracts/ContractAddendum";

interface FormValidation {
    success: boolean;
    errors: string[];
}

const useStyles = makeStyles({
    dialogActions: {
        marginTop: "16px",
        paddingTop: "12px",
        maxWidth: "100%",
        boxSizing: "border-box",
    },
});

const getCurrentDay = (): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

const getInitialNegotiationName = (contractDescription?: string, contractNumber?: string): string => {
    const contractName = (contractDescription || contractNumber || "").trim();
    return contractName ? `${contractName} - Negociación` : "Negociación";
};

const NegotiationAddendumFormComponent = (props: ComponentFunctions.Props) => {
    const styles = useStyles();

    // form status
    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [formValidationStatus, setFormValidationStatus] = useState<FormValidation | undefined>();

    // form fields
    const [addendumName, setAddendumName] = useState<string | undefined>(
        getInitialNegotiationName(props.Contract?.contractDescription, props.Contract?.contractNumber)
    );
    const [addendumDescription, setAddendumDescription] = useState<string | undefined>();
    const [addendumStartDate, setAddendumStartDate] = useState<Date | undefined>(getCurrentDay());
    const [addendumEndDate, setAddendumEndDate] = useState<Date | undefined>();

    useEffect(() => {
        if (!props.isOpen) {
            return;
        }

        if (props.CurrentAddendum) {
            setAddendumName(props.CurrentAddendum.contractAddendumName);
            setAddendumDescription(props.CurrentAddendum.contractAddendumDescription);
            setAddendumStartDate(props.CurrentAddendum.contractAddendumExpiryStartDate || getCurrentDay());
            setAddendumEndDate(props.CurrentAddendum.contractAddendumExpiryEndDate);
            return;
        }

        setAddendumName(getInitialNegotiationName(props.Contract?.contractDescription, props.Contract?.contractNumber));
        setAddendumDescription(undefined);
        setAddendumStartDate(getCurrentDay());
        setAddendumEndDate(undefined);
    }, [props.isOpen, props.CurrentAddendum, props.Contract?.contractDescription, props.Contract?.contractNumber]);

    useEffect(() => {
        if (props.isUpdatedSuccessfully || props.isCreatingAddendumSuccessfully) {
            resetForm();
            props.onClose();
        }
    }, [props.isUpdatedSuccessfully, props.isCreatingAddendumSuccessfully]);

    useEffect(() => {
        if (props.failOnCreatingAddendum || props.failOnUpdating) {
            setSubmittedForm(false);
        }
    }, [props.failOnCreatingAddendum, props.failOnUpdating]);

    const resetAddendumState = () => {
        props.ResetContractAddendumFormState(props.ContractId, props.CurrentAddendum?.id);
    };

    const handleCancelForm = () => {
        resetForm();
        resetAddendumState();
        props.onClose();
    };

    const handleDismissMessage = () => {
        resetForm();
        resetAddendumState();
    };

    const validateForm = (): boolean => {
        let validationResult = true;

        const currentValidation: FormValidation = { success: true, errors: [] };

        if (!addendumName || addendumName.trim() === "") {
            currentValidation.errors.push("Debe informar un nombre para la negociación");
            validationResult = false;
        }

        currentValidation.success = validationResult;
        setFormValidationStatus(currentValidation);

        return validationResult;
    };

    const handleSubmitForm = () => {
        setSubmittedForm(true);

        if (!validateForm()) {
            setSubmittedForm(false);
            return;
        }

        if (!props.CurrentAddendum) {
            const newAddendum: NewContractAddendum = {
                contractId: props.ContractId,
                contractAddendumName: addendumName || "",
                contractAddendumDescription: addendumDescription || "",
                contractAddendumExpiryStartDate: addendumStartDate,
                contractAddendumExpiryEndDate: addendumEndDate,
                contractAddendumType: ContractAddendumType.NegociationInstance,
            };

            props.CreateContractAddendum(props.ContractId, newAddendum);
            return;
        }

        const updatedAddendum: ContractAddendum = {
            id: props.CurrentAddendum.id,
            contractId: props.ContractId,
            contractAddendumName: addendumName || "",
            contractAddendumDescription: addendumDescription || "",
            contractAddendumExpiryStartDate: addendumStartDate,
            contractAddendumExpiryEndDate: addendumEndDate,
            contractAddendumType: ContractAddendumType.NegociationInstance,
            isActive: false,
        };

        props.UpdateContractAddendum(props.ContractId, updatedAddendum);
    };

    const resetForm = () => {
        setSubmittedForm(false);
        setFormValidationStatus(undefined);
        setAddendumName(getInitialNegotiationName(props.Contract?.contractDescription, props.Contract?.contractNumber));
        setAddendumDescription(undefined);
        setAddendumStartDate(getCurrentDay());
        setAddendumEndDate(undefined);
    };

    return (
        <Dialog
            open={props.isOpen}
            onOpenChange={(_, data) => {
                if (!data.open) {
                    handleCancelForm();
                }
            }}
        >
            <DialogSurface aria-describedby={undefined}>
                <DialogBody>
                    <DialogTitle>
                        {props.CurrentAddendum ? "Editar instancia de negociación" : "Nueva instancia de negociación"}
                    </DialogTitle>
                    <DialogContent>
                        {formValidationStatus?.success === false && (
                            <MessageComponent
                                message={"Verifique la información suministrada"}
                                type={MessageBarType.error}
                                itemList={formValidationStatus?.errors}
                                onDismiss={handleDismissMessage}
                            />
                        )}

                        {(props.failOnCreatingAddendum === true || props.failOnUpdating) && (
                            <MessageComponent
                                message={(props.errorOnCreatingAddendum || props.errorOnUpdating)?.ErrorMessage || "Ocurrió un error inesperado"}
                                type={MessageBarType.error}
                                itemList={(props.errorOnCreatingAddendum || props.errorOnUpdating)?.Errors}
                                onDismiss={handleDismissMessage}
                            />
                        )}

                        <Field label={"Nombre de negociación"} required>
                            <Input
                                size="large"
                                type="text"
                                id={"txt_negotiation_addendum_name"}
                                value={addendumName ?? ""}
                                required
                                maxLength={200}
                                onChange={(e) => setAddendumName(e.currentTarget.value)}
                                onFocus={(e) => e.currentTarget.select()}
                            />
                        </Field>

                        <Field label={"Descripción"}>
                            <Textarea
                                id={"txt_negotiation_addendum_description"}
                                value={addendumDescription}
                                onChange={(e) => setAddendumDescription(e.currentTarget.value)}
                                onFocus={(e) => e.currentTarget.select()}
                            />
                        </Field>

                        <DateTimeInput
                            label={"Vigencia desde"}
                            placeHolder={"dd/mm/yyyy"}
                            selectedDate={addendumStartDate ? new Date(addendumStartDate) : undefined}
                            onSelectDate={(date: Date | undefined) => setAddendumStartDate(date)}
                            isSubmitted={submittedForm}
                            isRequired={false}
                            errorMessage={undefined}
                        />

                        <DateTimeInput
                            label={"Vigencia hasta"}
                            placeHolder={"dd/mm/yyyy"}
                            selectedDate={addendumEndDate ? new Date(addendumEndDate) : undefined}
                            onSelectDate={(date: Date | undefined) => setAddendumEndDate(date)}
                            isSubmitted={submittedForm}
                            isRequired={false}
                            errorMessage={undefined}
                        />
                    </DialogContent>
                    <DialogActions className={styles.dialogActions}>
                        <Button onClick={handleCancelForm} disabled={submittedForm}>
                            Cancelar
                        </Button>
                        <Button appearance="primary" onClick={handleSubmitForm} disabled={submittedForm}>
                            {(props.isCreatingAddendum || props.isUpdating) && (
                                <>
                                    <Spinner size="extra-tiny" />&nbsp;
                                </>
                            )}
                            Guardar
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(NegotiationAddendumFormComponent as any);
